import React from 'react';

// Define the Apple ID configuration interface
interface AppleIDConfiguration {
    clientId: string;
    scope: string;
    redirectURI: string;
    state?: string;
    nonce?: string;
    usePopup?: boolean;
}

// Define the Apple sign-in response interface
interface AppleSignInResponse {
    authorization: {
        code: string;
        id_token: string;
        state?: string;
    };
    user?: {
        name?: {
            firstName: string;
            lastName: string;
        };
        email?: string;
    };
}

// Extend the Window interface to include AppleID
declare global {
    interface Window {
        AppleID: {
            auth: {
                init: (config: AppleIDConfiguration) => void;
                signIn: () => Promise<AppleSignInResponse>;
            };
        };
    }
}

interface AppleSignInButtonProps {
    toAbsoluteUrl: (path: string) => string;
    onAppleSignIn?: (data: { code: string; id_token: string; user?: any; state?: string }) => void; // NEW
    className?: string;
    isLoading?: boolean; // NEW
    loadingText?: string; // NEW
}


const AppleSignInButton: React.FC<AppleSignInButtonProps> = ({
    toAbsoluteUrl,
    onAppleSignIn,
    className = 'btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100',
    isLoading = false, // NEW
    loadingText = 'Signing in with Apple...'.toTranslatedDisplay() // NEW
}) => {
    const generateNonce = (): string => {
        const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz';
        let result = '';
        for (let i = 0; i < 16; i++) {
            result += chars[Math.floor(Math.random() * chars.length)];
        }
        return result;
    };

    // Your Apple configuration (replace with your actual Services ID)
    const APPLE_CONFIG = {
        clientId: 'com.zetran.mybooksai.backend', // Replace with your actual Services ID
        scope: 'name email',
        redirectURI: window.location.origin + '/auth/apple/callback/',
        usePopup: true,
        nonce: generateNonce(), // Add nonce
        prompt: 'select_account', // Force account selection
    };



    // Load Apple's JavaScript SDK
    const loadAppleSDK = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            // Check if SDK is already loaded
            if (window.AppleID) {
                resolve();
                return;
            }

            // Create script element
            const script = document.createElement('script');
            script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Apple SDK'));

            // Append to head
            document.head.appendChild(script);
        });
    };

    // Initialize Apple ID authentication
    const initializeAppleAuth = async (): Promise<void> => {
        try {
            await loadAppleSDK();

            // Initialize Apple ID with configuration
            window.AppleID.auth.init(APPLE_CONFIG);
        } catch (error) {
            console.error('Error initializing Apple ID:', error);
            throw error;
        }
    };

    // Handle Apple sign-in
    const handleAppleSignIn = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        e.preventDefault();

        try {
            // Initialize Apple Auth if not already done
            await initializeAppleAuth();

            // Trigger Apple sign-in
            const response: AppleSignInResponse = await window.AppleID.auth.signIn();

            // Extract the authorization code and ID token
            const { code, id_token, state } = response.authorization;

            // Extract user information (only available on first sign-in)
            const userInfo = response.user;

            // Send the authorization code to your backend for verification
            await sendTokenToBackend({
                code,
                id_token,
                user: userInfo,
                state
            });

        } catch (error: any) {
            // Handle different types of errors
            if (error.error === 'popup_closed_by_user') {
            } else {
                console.error('Error during Apple sign-in:', error);
            }
        }
    };

    // Send token to your backend
    // Send token to your backend
    const sendTokenToBackend = async (data: {
        code: string;
        id_token: string;
        user?: any;
        state?: string;
    }): Promise<void> => {
        try {
            // If onAppleSignIn callback is provided, use it instead of direct API call
            if (onAppleSignIn) {
                onAppleSignIn(data);
                return;
            }

            // Fallback to direct API call if no callback provided
            const response = await fetch('/api/auth/apple', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Failed to authenticate with backend');
            }

            const result = await response.json();

        } catch (error) {
            console.error('Error sending token to backend:', error);
        }
    };

    // Replace the return statement with this updated version:
    return (
        <div className='col-md-6'>
            {/* begin::Apple link */}
            <button
                type="button"
                onClick={handleAppleSignIn}
                className={className}
                disabled={isLoading}
            >
                {isLoading ? (
                    <>
                        <span className='spinner-border spinner-border-sm me-2'></span>
                        {loadingText}
                    </>
                ) : (
                    <>
                        <img
                            alt='Logo'
                            src={toAbsoluteUrl('media/svg/brand-logos/apple-black.svg')}
                            className='theme-light-show h-15px me-3'
                        />
                        <img
                            alt='Logo'
                            src={toAbsoluteUrl('media/svg/brand-logos/apple-black-dark.svg')}
                            className='theme-dark-show h-15px me-3'
                        />
                        {'Sign in with Apple'.toTranslatedDisplay()}
                    </>
                )}
            </button>
            {/* end::Apple link */}
        </div>
    );
};

export default AppleSignInButton;